import React, { useMemo } from 'react';
import { ColumnHelper, createColumnHelper } from '@tanstack/react-table';
import {
  Export,
  InternationalMarket,
  PropertyKey,
  SustainabilityReadinessLevel,
  TargetNumberDto,
  TargetNumberValueDto,
} from 'src/api/v2';
import Integer, { lowercaseFirstLetter } from '../components/EditableCells/Integer';
import BoolCell from '../components/EditableCells/BoolCell';
import { formatCurrencySimple, formatDateToNOString } from 'src/utils/FormatValue';
import EuEnviromentalGoals from '../components/EditableCells/EuEnviromentalGoals';
import { Link } from 'react-router-dom';
import { ROUTES } from 'src/navigation';
import { capitalizeFirstLetter } from 'src/utils/string';
import SivaPhaseTag from '../../ideas-and-companies/components/SivaPhaseTag';
import DropdownEnum from '../components/EditableCells/DropdownEnum';
import { useTranslation } from 'react-i18next';
import { transformToSrlReference } from 'src/features/siva/ideas-and-companies/utils';
import { internationalMarketTranslationSet } from '../../ideas-and-companies/constants';

export const newValueDto: TargetNumberValueDto = {
  admissionDate: undefined,
  businessModelDigitalization: undefined,
  businessModelExport: undefined,
  equity: undefined,
  esaSummary: undefined,
  euEnvironmentalGoals: undefined,
  internationalMarket: undefined,
  srl: undefined,
  womenInLeadingRole: undefined,
  businessModelSustainability: undefined,
  advisedAccessToOtherTools: undefined,
  internationalCooperation: undefined,
};

const unreachableCase = (_type: never) => [];

export const useAchievedGoalsColumns = (editMode: boolean, objectKey: PropertyKey, header: string) => {
  const { t: tSiva } = useTranslation('siva');
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TargetNumberDto>();

    return [
      columnHelper.accessor((row) => row.contractName, {
        id: 'name',
        cell: (info) => {
          const url = ROUTES.SIVA.IDEAS_AND_COMPANIES.COMPANY_DETAILS.INDEX(
            `${info.row.original.sivaContractId}`,
          );

          return <Link to={url}>{info.getValue() || info.row.original.sivaContractId}</Link>;
        },
      }),
      columnHelper.accessor('currentContractPhase', {
        id: 'phase',
        cell: (info) => <SivaPhaseTag phase={info.getValue()} />,
      }),
      ...calculateExtraColumns(objectKey, columnHelper, editMode, header, tSiva),
    ];
  }, [editMode, header, objectKey, tSiva]);

  return columns;
};

const getBusinessModelExportText = (exp: Export, tSiva: (key: string) => string) => {
  switch (exp) {
    case Export.Potensiale:
      return tSiva('report.goals.export.potensiale');
    case Export.Underleverandor:
      return tSiva('report.goals.export.underleverandor');
    default:
      return exp;
  }
};

const calculateExtraColumns = (
  objectKey: PropertyKey,
  columnHelper: ColumnHelper<TargetNumberDto>,
  editMode: boolean,
  header: string,
  tSiva: (key: string) => string,
) => {
  switch (objectKey) {
    case PropertyKey.Equity:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <Integer targetNumber={info.getValue()} propertyKey={objectKey} editMode={editMode} />
          ),
        }),
      ];
    case PropertyKey.Srl:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: 'SRL-nivå',
          enableSorting: false,
          cell: (info) => (
            <DropdownEnum
              targetNumber={info.getValue()}
              propertyKey={objectKey}
              editMode={editMode}
              options={Object.values(SustainabilityReadinessLevel).map((srl) => ({
                text: transformToSrlReference(srl),
                value: srl,
              }))}
            />
          ),
        }),
      ];
    case PropertyKey.AdmissionDate:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => {
            const targetNumber = info.getValue();
            return targetNumber.valueDto && targetNumber.valueDto[lowercaseFirstLetter(objectKey)]
              ? capitalizeFirstLetter(
                  formatDateToNOString(targetNumber.valueDto[lowercaseFirstLetter(objectKey)]),
                )
              : '';
          },
        }),
      ];
    case PropertyKey.BusinessModelDigitalization:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <BoolCell targetNumber={info.getValue()} objectKey={objectKey} editMode={editMode} />
          ),
        }),
      ];
    case PropertyKey.BusinessModelExport:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <DropdownEnum
              targetNumber={info.getValue()}
              propertyKey={objectKey}
              editMode={editMode}
              options={Object.values(Export).map((exp) => ({
                text: getBusinessModelExportText(exp, tSiva),
                value: exp,
              }))}
            />
          ),
        }),
      ];
    case PropertyKey.EsaSummary:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: 'Markedsverdi',
          enableSorting: false,
          cell: (info) => {
            const targetNumber = info.getValue();
            return targetNumber.valueDto && targetNumber.valueDto[lowercaseFirstLetter(objectKey)]
              ? formatCurrencySimple(targetNumber.valueDto[lowercaseFirstLetter(objectKey)].marketValue)
              : '';
          },
        }),
        columnHelper.accessor((row) => row, {
          id: '__ekstra2',
          header: 'Egenandel',
          enableSorting: false,
          cell: (info) => {
            const targetNumber = info.getValue();
            return targetNumber.valueDto && targetNumber.valueDto[lowercaseFirstLetter(objectKey)]
              ? formatCurrencySimple(
                  targetNumber.valueDto[lowercaseFirstLetter(objectKey)].invoicedDeductible,
                )
              : '';
          },
        }),
        columnHelper.accessor((row) => row, {
          id: '__ekstra3',
          header: 'Innovasjonsstøtte',
          enableSorting: false,
          cell: (info) => {
            const targetNumber = info.getValue();
            return targetNumber.valueDto && targetNumber.valueDto[lowercaseFirstLetter(objectKey)]
              ? formatCurrencySimple(targetNumber.valueDto[lowercaseFirstLetter(objectKey)].innovationSupport)
              : '';
          },
        }),
      ];
    case PropertyKey.EuEnvironmentalGoals:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          minSize: 300,
          enableSorting: false,
          cell: (info) => (
            <EuEnviromentalGoals targetNumber={info.getValue()} objectKey={objectKey} editMode={editMode} />
          ),
        }),
      ];
    case PropertyKey.InternationalMarket:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <DropdownEnum
              targetNumber={info.getValue()}
              propertyKey={objectKey}
              editMode={editMode}
              options={Object.values(InternationalMarket).map((im) => ({
                text: tSiva(internationalMarketTranslationSet[im]),
                value: im,
              }))}
            />
          ),
        }),
      ];
    case PropertyKey.WomenInLeadingRole:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <BoolCell targetNumber={info.getValue()} objectKey={objectKey} editMode={editMode} />
          ),
        }),
      ];
    case PropertyKey.BusinessModelSustainability:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <BoolCell targetNumber={info.getValue()} objectKey={objectKey} editMode={editMode} />
          ),
        }),
      ];
    case PropertyKey.AdvisedAccessToOtherTools:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <BoolCell targetNumber={info.getValue()} objectKey={objectKey} editMode={editMode} />
          ),
        }),
      ];
    case PropertyKey.InternationalCooperation:
      return [
        columnHelper.accessor((row) => row, {
          id: '__ekstra',
          header: header,
          enableSorting: false,
          cell: (info) => (
            <BoolCell targetNumber={info.getValue()} objectKey={objectKey} editMode={editMode} />
          ),
        }),
      ];
    default:
      //Tester noe her. Når backend blir oppgradert, så vil denne klage over uhåndterte cases. Bør gi oss en pekepinn på at vi må oppdatere switch
      return unreachableCase(objectKey);
  }
};
